import { ApiError } from "../../common/api/ApiClient";
import {
  ResearchTaskSchema,
  ResearchTaskStatusResponse,
} from "../../common/models/schema/ResearchTask";
import { useApiClient } from "../../common/hooks/useApiClient";
import { useEffect, useRef, useState } from "react";
import { ResearchTaskStatus } from "../../common/models/schema/ResearchTask";
import { useResearchTasks } from "../../common/contexts/ResearchTaskProvider";
import {
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  Alert,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { PersonExtendedWIthTags } from "../../common/models/schema/UpdatePersonResponse";

const apiPath = "/nationbuilder/research-person";

type ResearchTaskProps = {
  personId: string;
  taskResult: PersonExtendedWIthTags | null;
};

const ResearchTask = ({ personId, taskResult = null }: ResearchTaskProps) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const interval = useRef<NodeJS.Timeout | null>(null);
  const apiClient = useApiClient();
  const { getTaskById, updateTask, deleteTask } = useResearchTasks();
  const navigate = useNavigate();

  useEffect(() => {
    const task = getTaskById(personId);
    if (task && task.task_status === ResearchTaskStatus.FAILED) {
      setError(task.error);
      setLoading(false);
    } else if (taskResult == null) {
      setLoading(true);
      interval.current = setInterval(() => {
        pollForStatus();
      }, 30000);
    } else {
      setSuccess(true);
    }

    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, []);

  const pollForStatus = async () => {
    try {
      const statusResponse = await apiClient.put<ResearchTaskStatusResponse>(
        apiPath + "/status",
        {
          person_id: Number(personId),
        },
      );

      if (statusResponse.data.task_status === ResearchTaskStatus.RUNNING) {
        return;
      }

      const task = getTaskById(personId);
      const updatedTask: ResearchTaskSchema = {
        ...task,
        task_result: statusResponse.data.task_result,
        task_status: statusResponse.data.task_status,
        updated_at: new Date(),
      };
      updateTask(personId, updatedTask);
      setSuccess(true);
      if (interval.current) {
        clearInterval(interval.current);
      }
      setLoading(false);
    } catch (err) {
      const apiError = err as ApiError;
      if (interval.current) {
        clearInterval(interval.current);
      }
      const task = getTaskById(personId);
      const updatedTask: ResearchTaskSchema = {
        ...task,
        task_status: ResearchTaskStatus.FAILED,
        updated_at: new Date(),
        error: apiError.data?.detail || apiError.message,
      };
      updateTask(personId, updatedTask);
      setError(apiError.data?.detail || apiError.message);
      setLoading(false);
    }
  };

  const handleAccess = () => {
    navigate(`/dashboard/ResearchPersonDetails/${personId}`);
  };

  const handleDelete = () => {
    if (interval.current) {
      clearInterval(interval.current);
    }
    deleteTask(personId);
  };

  return (
    <Paper elevation={1} sx={{ p: 4, minWidth: 500 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        {/* Left side: two rows (ID, Status) */}
        <Stack spacing={0.75} flexGrow={1} minWidth={0}>
          <Typography variant="subtitle1" noWrap>
            Person ID: {personId}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" minHeight={24}>
            {loading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2">Processing...</Typography>
              </Stack>
            )}
            {!loading && success && (
              <Chip size="small" color="success" label="Success" />
            )}
            {!loading && !success && !error && (
              <Chip size="small" color="warning" label="Pending" />
            )}
            {error && <Chip size="small" color="error" label="Error" />}
          </Stack>
          {error && (
            <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </Stack>

        {/* Right side: same-row buttons (Access, X) */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
        >
          <Button
            onClick={() => handleAccess()}
            disabled={loading || error !== null}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ ml: 1 }}
          >
            Access
          </Button>
          <Button
            onClick={() => handleDelete()}
            disabled={loading}
            color="error"
            variant="outlined"
            size="small"
            sx={{ ml: 1 }}
          >
            Delete
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default ResearchTask;
